// useShortcuts — registracia klavesovych skratiek + auto-zapis do cheatsheet registry
// Ustava sek. 54.4-5, 54.8-9: globalne + modulove skratky, registracne tabulka pre cheatsheet (?)
//
// API:
//   useShortcuts([
//     { keys: 'mod+s',   label: 'Ulozit',           scope: 'global', handler: () => save() },
//     { keys: 'mod+k',   label: 'Quick-jump',       scope: 'global', handler: () => openCmdBar() },
//     { keys: '?',       label: 'Cheatsheet',       scope: 'global', handler: () => openCheatsheet() }
//   ])
//
//   const all = useShortcutsRegistry()   // pre ShortcutCheatsheet
//
// POZOR: pole `shortcuts` musi mat **stabilnu dlzku** per komponent (React rules-of-hooks).
// Pre dynamicke skratky pouzite klucovy wrap (kazda skratka v samostatnom komponente).

import { useEffect, useReducer } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { IS_MAC } from '../theme.js'

// Globalny registry: id -> {keys, label, scope, description}
const _registry = new Map()
const _listeners = new Set()
let _nextId = 1

function _notify() {
  _listeners.forEach(fn => fn())
}

// Prelozit 'mod' na 'ctrl' / 'meta' podla OS (ustava 54.4)
function _resolveKeys(keys) {
  if (typeof keys !== 'string') return keys
  return keys.replace(/\bmod\b/gi, IS_MAC ? 'meta' : 'ctrl')
}

// Pre cheatsheet display — 'mod+s' -> '⌘ + S' / 'Ctrl + S'
export function formatKeys(keys) {
  if (!keys) return ''
  return keys
    .split(',')[0]
    .trim()
    .split('+')
    .map(k => {
      const lk = k.toLowerCase().trim()
      if (lk === 'mod')   return IS_MAC ? '⌘' : 'Ctrl'
      if (lk === 'meta')  return '⌘'
      if (lk === 'ctrl')  return 'Ctrl'
      if (lk === 'shift') return 'Shift'
      if (lk === 'alt')   return IS_MAC ? '⌥' : 'Alt'
      if (lk === 'enter') return '↵'
      if (lk === 'esc' || lk === 'escape') return 'Esc'
      if (lk === 'space') return 'Space'
      if (lk === 'arrowup')    return '↑'
      if (lk === 'arrowdown')  return '↓'
      if (lk === 'arrowleft')  return '←'
      if (lk === 'arrowright') return '→'
      return k.length === 1 ? k.toUpperCase() : k
    })
    .join(' + ')
}

// Hlavny hook — registruje pole shortcuts (musi mat stabilnu dlzku)
export function useShortcuts(shortcuts) {
  // Registracia do globalneho registry pre cheatsheet
  useEffect(() => {
    const ids = shortcuts.map(s => {
      const id = _nextId++
      _registry.set(id, {
        id,
        keys: s.keys,
        label: s.label || '',
        scope: s.scope || 'global',
        description: s.description || ''
      })
      return id
    })
    _notify()
    return () => {
      ids.forEach(id => _registry.delete(id))
      _notify()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortcuts.map(s => s.keys + '|' + s.label + '|' + s.scope).join('&')])

  // Pre kazdy shortcut samostatny useHotkeys
  // Pole musi mat stabilnu dlzku per komponent (rules-of-hooks)!
  shortcuts.forEach((s) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useHotkeys(
      _resolveKeys(s.keys),
      (e) => {
        if (s.preventDefault !== false) e.preventDefault()
        s.handler(e)
      },
      {
        enableOnFormTags: s.enableOnFormTags !== false,
        preventDefault: s.preventDefault !== false,
        ...s.options
      },
      [s.handler]
    )
  })
}

// Singular varianta — pre jednu skratku (v ramci komponentu volana iba raz)
export function useShortcut(keys, handler, options = {}) {
  useShortcuts([{ keys, handler, ...options }])
}

// Hook pre cheatsheet — vrati aktualny snapshot registry
export function useShortcutsRegistry() {
  const [, force] = useReducer((x) => x + 1, 0)
  useEffect(() => {
    _listeners.add(force)
    return () => _listeners.delete(force)
  }, [])
  return Array.from(_registry.values())
}

export default useShortcuts
